import _ from 'lodash';
import Mustache from 'mustache';
import { encode } from 'html-entities';
import TokenizeThis from 'tokenize-this';
import StringBuffer from '@api-consent/util/StringBuffer';

type Context = object;

export interface TranspilerOptions {
  compatibility?: boolean; // WORKAROUND: compatibility flag for MCE and DDC
  includeCustomerAttributes?: boolean; // WORKAROUND: compatibility flag for PrintLifecycle/EPOS
  context?: Context;
}

const emptyOptions: TranspilerOptions = {
  context: {},
};

type NodeType = 'root'
| 'emphasis' | 'superscript' | 'subscript'
| 'variable' | 'break' | 'link'
| 'annotation' | 'annotation:key' | 'annotation:value'
| 'reference' | 'reference:content' | 'reference:title'
| 'paragraph:1' | 'paragraph:2' | 'paragraph:3'
| 'text:1' | 'text:2' | 'text:3';

interface Node {
  type?: NodeType;
  token?: string;
  index?: number;
  children?: Node[];
}

interface NodeVariable extends Node {
  variable: string;
}

interface NodeStyled extends Node {
  style?: string;
}

interface NodeLink extends Node {
  url: string;
}

interface NodeAnnotation extends Node {
}

interface NodeReference extends Node {
  refId: string; // e.g., "produkte"
  refType: string; // e.g., "cattr"
}

// ================================================================================================
// tree
// ================================================================================================

interface VisitorResponse {
  continueWalking: boolean; // `false` to stop walking
}

type Visitor = (node: Node) => VisitorResponse;

const walk = (
  node: Node,
  visitorPre: Visitor,
  visitorPost: Visitor = () => ({ continueWalking: true }),
) => {
  const { continueWalking } = visitorPre(node);
  if (!continueWalking) return;
  const { children = [] } = node; // children might have been changed by visitorPre()
  children.forEach((child) => {
    walk(child, visitorPre, visitorPost);
  });
  visitorPost(node);
};

// ================================================================================================
// tokenizer
// ================================================================================================

const tokenizer = new TokenizeThis({
  shouldTokenize: [
    '**',
    '\\',
    '~',
    '^',
    '(', ')',
    '[', ']',
    '|',
    '$$', '$$$', '$$$$',
    '%%', '%%%', '%%%%',
    '{{', '}}',
    '{{{', '}}}',
  ],
  shouldMatch: [], // ['"', '\'']
  shouldDelimitBy: [], // [' ', '\n', '\r', '\t', ':', ','],
  convertLiterals: false, // strings only
});

const tokenize = (str: string): Node[] => {
  if (!str) return [];
  const tokens: Node[] = [];
  tokenizer.tokenize(str, (token, _surroundedBy, index) => {
    if (typeof token !== 'string') return; // should never happen because convertLiterals===false
    tokens.push({ token, index }); // `type` still undefined
  });
  // console.log(inspect(tokens, { depth: null, colors: true })); // debug
  return tokens;
};

// ================================================================================================
// parser
// ================================================================================================

const NOTFOUND = -1;

const find = (
  nodes: Node[],
  token: string | string[] | undefined,
  start = 0,
  stopTokens: string[] = [],
) => {
  if (!token) return NOTFOUND;
  const compare = Array.isArray(token)
    ? (t: string) => token.includes(t)
    : (t: string) => t === token;
  const stop = (t: string) => stopTokens.includes(t);
  for (let index = start; index < nodes.length; index += 1) {
    const t = nodes[index].token;
    if (t && stop(t)) return NOTFOUND;
    if (t && compare(t)) return index;
  }
  return NOTFOUND;
};

const found = (...nn: number[]) => nn.reduce((acc, n) => (acc && n >= 0), true);

const after = (n: number) => n + 1;

const parseVariables = ({ context }: TranspilerOptions) => ({ children = [] }: Node): VisitorResponse => {
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos < childrenOld.length;) {
    const open = find(childrenOld, ['{{', '{{{'], pos);
    const closeToken = found(open) && childrenOld[open].token === '{{' ? '}}' : '}}}';
    const close = found(open) ? find(childrenOld, closeToken, after(open)) : NOTFOUND;
    if (found(open, close)) {
      children.push(...childrenOld.slice(pos, open));
      const variable = childrenOld[after(open)]?.token?.trim();
      if (variable && close > after(open)) {
        // exclude empty variable
        const value = `${_.get(context, variable, '')}`; // ensure string
        const node: NodeVariable = {
          type: 'variable',
          variable,
          children: tokenize(value),
        };
        children.push(node);
      }
      pos = after(close);
    } else {
      children.push(...childrenOld.slice(pos));
      pos = childrenOld.length; // break
    }
  }
  return { continueWalking: true };
};

const parseBreak = () => ({ children = [] }: Node): VisitorResponse => {
  children
    .filter(({ type, token }) => !type && token === '\\')
    .forEach((child) => {
      child.type = 'break'; // eslint-disable-line no-param-reassign
    });
  return { continueWalking: true };
};

const parseEnclosing = () => ({ children = [] }: Node): VisitorResponse => {
  const types: Record<string, NodeType> = {
    '**': 'emphasis',
    '^': 'superscript',
    '~': 'subscript',
    '%%': 'paragraph:1',
    '%%%': 'paragraph:2',
    '%%%%': 'paragraph:3',
    $$: 'text:1',
    $$$: 'text:2',
    $$$$: 'text:3',
  };
  const tokens = Object.keys(types);
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos < childrenOld.length;) {
    const open = find(childrenOld, tokens, pos);
    const token = found(open) ? childrenOld[open].token : undefined;
    const close = found(open) ? find(childrenOld, token, after(open)) : NOTFOUND;
    if (token && found(open, close)) {
      const nodeType = types[token];
      const style = (nodeType === 'paragraph:1' || nodeType === 'text:1')
        && childrenOld[open + 1]?.token === '['
        && childrenOld[open + 3]?.token === ']'
        ? childrenOld[open + 2]?.token?.trim() ?? ''
        : undefined;
      children.push(...childrenOld.slice(pos, open));
      const node: NodeStyled = {
        type: nodeType,
        style,
        children: childrenOld.slice(open + (style ? 4 : 1), close),
      };
      children.push(node);
      pos = after(close);
    } else {
      children.push(...childrenOld.slice(pos));
      pos = childrenOld.length; // break
    }
  }
  return { continueWalking: true };
};

const parseLink = () => ({ children = [] }: Node): VisitorResponse => {
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos < childrenOld.length;) {
    const textOpen = find(childrenOld, '[', pos);
    const textClose = found(textOpen) ? find(childrenOld, ']', after(textOpen), ['[']) : NOTFOUND;
    const urlOpen = found(textClose) && childrenOld[after(textClose)]?.token === '(' ? after(textClose) : NOTFOUND;
    const urlClose = found(urlOpen) ? find(childrenOld, ')', after(urlOpen), ['(']) : NOTFOUND;
    if (found(textOpen, textClose, urlOpen, urlClose)) {
      children.push(...childrenOld.slice(pos, textOpen));
      const node: NodeLink = {
        type: 'link',
        children: childrenOld.slice(after(textOpen), textClose),
        url: childrenOld
          .slice(after(urlOpen), urlClose)
          .map(({ token, children: kids = [] }) => token ?? kids[0].token)
          .join(''),
      };
      children.push(node);
      pos = after(urlClose);
    } else if (found(textOpen)) {
      children.push(childrenOld[pos]);
      pos = after(pos);
    } else {
      children.push(...childrenOld.slice(pos));
      pos = childrenOld.length; // break
    }
  }
  return { continueWalking: true };
};

// Annotation nodes:
// Can either be [Text|Tooltip] (annotation) or
// [Text|id|referencetype] as used by eg. customer attributes.
// Since both use square brackets we parse them both here.
// eslint-disable-next-line max-len
const parseAnnotationAndReference = ({ context }: TranspilerOptions) => ({ children = [] }: Node): VisitorResponse => {
  const childrenOld = children.splice(0, children.length); // empty
  for (let pos = 0; pos < childrenOld.length;) {
    const open = find(childrenOld, '[', pos);
    const separator = found(open) ? find(childrenOld, '|', after(open), [']']) : NOTFOUND;
    const separator2 = found(separator) ? find(childrenOld, '|', after(separator), [']']) : NOTFOUND;
    const close = found(separator) ? find(childrenOld, ']', after(found(separator2) ? separator2 : separator), ['|', '[']) : NOTFOUND;
    if (found(open, separator, separator2, close)) { // [x|y|z] Reference
      children.push(...childrenOld.slice(pos, open));
      const refId = childrenOld
        .slice(after(separator), separator2)
        .map(({ token, children: kids = [] }) => token ?? kids[0].token)
        .join('')
        .trim();
      const refType = childrenOld.slice(after(separator2), close)
        .map(({ token, children: kids = [] }) => token ?? kids[0].token)
        .join('')
        .trim();
      const path = `${refType}.${refId}.title`; // e.g., `cattr.foo.title`
      const value = `${_.get(context, path, '')}`; // ensure string
      const node: NodeReference = {
        type: 'reference',
        refId,
        refType,
        children: [{
          type: 'reference:content',
          children: childrenOld.slice(after(open), separator),
        }, {
          type: 'reference:title',
          children: tokenize(value),
        }],
      };
      children.push(node);
      pos = after(close);
    } else if (found(open, separator, close)) { // [x|y] Annotation
      children.push(...childrenOld.slice(pos, open));
      const node: NodeAnnotation = {
        type: 'annotation',
        children: [{
          type: 'annotation:key',
          children: childrenOld.slice(after(open), separator),
        }, {
          type: 'annotation:value',
          children: childrenOld.slice(after(separator), close),
        }],
      };
      children.push(node);
      pos = after(close);
    } else { // no further [x|y] or [x|y|z]
      children.push(...childrenOld.slice(pos));
      pos = childrenOld.length; // break
    }
  }
  return { continueWalking: true };
};

const parse = (str: string, options: TranspilerOptions): Node => {
  if (!str) return { type: 'root', children: [] };
  const root: Node = { type: 'root', children: tokenize(str) };
  for (let i = 0; i < 2; i += 1) { // twice !!
    walk(root, parseVariables(options));
    walk(root, parseBreak());
    walk(root, parseEnclosing());
    walk(root, parseLink());
    walk(root, parseAnnotationAndReference(options));
  }
  return root;
};

// ================================================================================================
// html generator
// ================================================================================================

const mapOpen: Record<string, string> = {
  root: '<div>',
  emphasis: '<em>',
  superscript: '<sup>',
  subscript: '<sub>',
  break: '<br />',
};

const mapClose: Record<string, string> = {
  root: '</div>',
  emphasis: '</em>',
  superscript: '</sup>',
  subscript: '</sub>',
  link: '</a>',
  'paragraph:1': '</div>',
  'paragraph:2': '</div>',
  'paragraph:3': '</div>',
  'text:1': '</span>',
  'text:2': '</span>',
  'text:3': '</span>',
  variable: '</span>',
};

const generatePost = ({
  target,
  options,
}: {
  target: StringBuffer;
  options: TranspilerOptions;
}) => ({ type }: Node): VisitorResponse => {
  const { compatibility = false } = options;
  if (type === 'variable' && compatibility) return { continueWalking: true };
  const tag = type ? mapClose[type] : undefined;
  target.append(tag ?? '');
  return { continueWalking: true };
};

const generatePre = ({
  target,
  withEncode,
  options,
}: {
  target: StringBuffer;
  withEncode: boolean;
  options: TranspilerOptions;
// eslint-disable-next-line sonarjs/cognitive-complexity
}) => (node: Node): VisitorResponse => {
  const { compatibility = false, includeCustomerAttributes = true } = options;
  const { type, token, children = [] } = node;
  if (type === 'link') {
    const { url } = node as NodeLink;
    const aHref = url ?? '#';
    const aTarget = aHref && !aHref.startsWith('#') ? ' target="_blank"' : '';
    target.append(`<a href="${aHref}"${aTarget}>`);
  } else if (type === 'annotation') {
    const [annotationKey, annotationValue] = children;
    const annotationKeyHtml = new StringBuffer();
    const annotationValueHtml = new StringBuffer();
    walk(
      annotationKey,
      generatePre({
        target: annotationKeyHtml,
        withEncode: true,
        options,
      }),
      generatePost({
        target: annotationKeyHtml,
        options,
      }),
    );
    walk(
      annotationValue,
      generatePre({
        target: annotationValueHtml,
        withEncode: false,
        options,
      }),
      generatePost({
        target: annotationValueHtml,
        options,
      }),
    );
    const annotationKeyHtmlStr = annotationKeyHtml.toString();
    const annotationValueHtmlStr = encode(annotationValueHtml.toString());
    target.append(
      `<abbr title="${annotationValueHtmlStr}">${annotationKeyHtmlStr}</abbr>`,
    );
    return { continueWalking: false }; // stop walking --> annotation handled separately
  } else if (type === 'reference') {
    const { refId, refType } = node as NodeReference;
    const [referenceContent, referenceTitle] = children;
    const referenceContentHtml = new StringBuffer();
    const referenceTitleHtml = new StringBuffer();
    walk(
      referenceContent,
      generatePre({
        target: referenceContentHtml,
        withEncode: false,
        options,
      }),
      generatePost({
        target: referenceContentHtml,
        options,
      }),
    );
    walk(
      referenceTitle,
      generatePre({
        target: referenceTitleHtml,
        withEncode: true,
        options,
      }),
      generatePost({
        target: referenceTitleHtml,
        options,
      }),
    );
    const referenceContentHtmlStr = referenceContentHtml.toString();
    const referenceTitleHtmlStr = referenceTitleHtml.toString()
      // if using HTML inside an HTML attribute, escape <, > and " (but NOT &)
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
    let html;
    if (compatibility || !includeCustomerAttributes) {
      html = referenceTitleHtmlStr
        ? `<abbr title="${referenceTitleHtmlStr}">${referenceContentHtmlStr}</abbr>`
        : referenceContentHtmlStr;
    } else {
      html = referenceTitleHtmlStr
        ? `<abbr data-coms-type="${refType}" data-coms-id="${refId}" title="${referenceTitleHtmlStr}">${referenceContentHtmlStr}</abbr>`
        : `<abbr data-coms-type="${refType}" data-coms-id="${refId}">${referenceContentHtmlStr}</abbr>`;
    }
    target.append(html);
    return { continueWalking: false }; // stop walking --> reference handled separately
  } else if (type?.startsWith('paragraph')) {
    const { style } = node as NodeStyled;
    const match = /paragraph:(?<subtype>\d)/.exec(type);
    const subtype = match?.groups?.subtype ?? '1';
    const clazz = style || `coms-paragraph-${subtype}`;
    target.append(`<div class="${clazz}">`);
  } else if (type?.startsWith('text')) {
    const { style } = node as NodeStyled;
    const match = /text:(?<subtype>\d)/.exec(type);
    const subtype = match?.groups?.subtype ?? '1';
    const clazz = style || `coms-text-${subtype}`;
    target.append(`<span class="${clazz}">`);
  } else if (type === 'variable' && compatibility) {
    // do nothing
  } else if (type === 'variable') {
    const { variable } = node as NodeVariable;
    const clazz = `coms-${variable?.replace(/\W/g, '-') ?? ''}`;
    target.append(`<span class="${clazz}">`);
  } else {
    const tag = type ? mapOpen[type] : undefined;
    const content = tag ?? (withEncode ? encode(token) : token);
    target.append(content ?? '');
  }
  return { continueWalking: true };
};

// ================================================================================================
// transpiler functions
// ================================================================================================

export const markup2Html = (
  str: string,
  options: TranspilerOptions = emptyOptions,
): string => {
  // 1. Texthervorhebung `**wichtig**` --> `<em>wichtig</em>`
  // 2. Zeilenumbruch `\` --> `<br />`
  // 3. Tiefgestellt `O~2~` --> `O<sub>2<sub>`
  // 4. Hochgestellt `x^2^` --> `x<sup>2<sup>`
  // 5. Link `[click here](http://example.com)` --> `<a href="http://example.com" target="_blank">click here</a>`
  //    - es sind nur absolute Links erlaubt
  // 6. Annotation `[o2|o2 Gmbh & Co. OHG]` --> `<abbr title="o2 Gmbh &amp; Co. OHG">o2</abbr>`
  //    - da hier z.B. Tiefstellung vorkommen kann, sollte im Browser nicht die native Darstellung
  //      verwendet werden
  // 6.2. Reference `[Produkte|products|cattr]` --> `<abbr data-coms-type="cattr" data-coms-id="products" title="bla">Produkte</abbr>`
  // 7. Paragraph, Text, Style Class
  //    - `%%foo%%` --> `<div class="coms-paragraph-1">foo</div>`
  //    - `$$foo$$` --> `<span class="coms-text-1">foo</span>`
  //    - `%%[foo]bar%%` --> `<div class="foo">bar</div>`
  //    - `$$[foo]bar$$` --> `<span class="foo">bar</span>`
  if (!str) return str;
  const root = parse(str, options);
  // console.log(inspect(root, { depth: null, colors: true })); // debug
  const html = new StringBuffer();
  walk(
    root,
    generatePre({ target: html, withEncode: true, options }),
    generatePost({ target: html, options }),
  );
  // console.log(inspect(html, { depth: null, colors: true })); // debug
  return html.toString();
};

export const markup2markup = (
  str: string,
  options: TranspilerOptions = emptyOptions,
): string => {
  if (!str) return str;
  const { context = {}, compatibility = false, includeCustomerAttributes = true } = options;
  const cattrRegExp = /\[(?<refText>[^|\]]*)\|(?<refId>[^|\]]*)\|(?<refType>[^|\]]*)\]/g;
  const refs2tooltips = compatibility || !includeCustomerAttributes
    // `[x|y|cattr]` => `[x|{{{references.cattr.y.title}}}]`
    ? str.replaceAll(cattrRegExp, '[$1|{{{$3.$2.title}}}]')
    : str;
  const varsReplacedOnce = Mustache.render(refs2tooltips, context);
  const varsReplacedAgain = Mustache.render(varsReplacedOnce, context);
  return compatibility
    // remove style from paragraph (%%) or text ($$)
    ? varsReplacedAgain.replaceAll(/(%{2}|\${2})(\[[\w -]+\])/g, '$1')
    : varsReplacedAgain;
};
